import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Heading,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  CompanyStatus,
  RepresentativeRole,
  useUpdateCompanyMutation,
} from '@blockpulse3/graphql/hooks';
import {
  formatDate,
  formatNationality,
  formatNumberCurrency,
  isCompanyComplete,
} from '@blockpulse3/helpers';
import {
  CardIdentity,
  ErrorQueryCard,
  useErrorToast,
  useSuccessToast,
  useWarningToast,
} from '@blockpulse3/ui/commons';

import { useGetOnboardingExistingCompany } from '../../hooks/useGetOnboardingExistingCompany';

type Props = unknown;

/**
 * OnboardingExistingCompanySummaryStep.
 * Summary of the existing company creation.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function OnboardingExistingCompanySummary(): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nRoleValues = useTranslations('RoleValues');
  const i18nSummaryStep = useTranslations('ImportSummaryStep');

  const errorToast = useErrorToast();
  const warningToast = useWarningToast();
  const successToast = useSuccessToast();

  const navigate = useNavigate();
  const { companyId = '' } = useParams();

  const { data, loading, error } = useGetOnboardingExistingCompany();

  const [updateCompany] = useUpdateCompanyMutation();

  const handleStepCancel = (): void => {
    navigate(routes.onboardings.company.access.fmt(companyId));
  };

  const handleStepSubmit = (): void => {
    if (isCompanyComplete(data?.company)) {
      updateCompany({
        variables: {
          updateCompanyInput: {
            companyId,
            status: CompanyStatus.RUNNING,
          },
        },
        onCompleted: async () => {
          successToast({ title: i18nSummaryStep('Toast.ExistingCompanySuccess') });
          navigate(routes.workspace.fmt(companyId));
        },
        onError: () => {
          errorToast({ title: i18nSummaryStep('Toast.ExistingCompanyError') });
        },
      });
    } else {
      warningToast({ title: i18nSummaryStep('Toast.ExistingCompanyWarning') });
      navigate(routes.onboardings.company.infos.fmt(companyId));
    }
  };

  if (loading) {
    return <Skeleton height="350px" width="full" />;
  }

  if (error) {
    return <ErrorQueryCard width="full" />;
  }

  if (!data) {
    return <ErrorQueryCard width="full" />;
  }

  const presidents = data.company.companyRepresentative.filter((rep) => {
    return (
      rep.role === RepresentativeRole.PRESIDENT || rep.role === RepresentativeRole.COMPANY_MANAGER
    );
  });

  const presidentsRepresentativeIdentityIds = presidents.map(
    (rep) => rep.representativeIdentity.id,
  );

  const otherRoles = data.company.companyRepresentative.filter((rep, i) => {
    return (
      rep.role !== RepresentativeRole.PRESIDENT &&
      rep.role !== RepresentativeRole.COMPANY_MANAGER &&
      !presidentsRepresentativeIdentityIds.find((id) => id === rep.representativeIdentity.id)
    );
  });

  return (
    <Card variant="divider-top" width="full">
      <CardHeader>
        <Heading size="lg">{i18nSummaryStep('Title')}</Heading>
      </CardHeader>
      <Divider />
      <CardBody as={Stack} spacing="4">
        <Stack spacing="4">
          <Text fontSize="xl" fontWeight="bold">
            {i18nSummaryStep('CompanyInfosTitle')} :
          </Text>
          <Stack bg="gray.50" borderRadius="md" p="3" spacing="1">
            <Text>
              <Text as="span" fontWeight="bold" mr="2">
                {i18nSummaryStep('NameField')} :
              </Text>
              {data.company.name}
            </Text>
            <Text>
              <Text as="span" fontWeight="bold" mr="2">
                {i18nSummaryStep('NationalityField')} :
              </Text>
              {formatNationality(data.company.address?.country || 'FRA')}
            </Text>
            <Text>
              <Text as="span" fontWeight="bold" mr="2">
                {i18nSummaryStep('IdentificationNumberField')} :
              </Text>
              {data.company?.registrationNumber}
            </Text>
            <Text>
              <Text as="span" fontWeight="bold" mr="2">
                {i18nSummaryStep('FormField')} :
              </Text>
              {data.company?.corporateForm}
            </Text>
            <Text>
              <Text as="span" fontWeight="bold" mr="2">
                {i18nSummaryStep('CreationDateField')} :
              </Text>
              {formatDate(data.company?.creationDate, 'D MMMM YYYY')}
            </Text>
            <Text>
              <Text as="span" fontWeight="bold" mr="2">
                {i18nSummaryStep('SocialCapitalField')} :
              </Text>
              {formatNumberCurrency(data.company?.shareCapital || '')}
            </Text>
          </Stack>
        </Stack>
        <Stack spacing="4">
          <Text fontSize="xl" fontWeight="bold">
            {i18nSummaryStep('RolesTitle')}
          </Text>
          {presidents.map((president) => {
            const identity = president.representativeIdentity;
            const subTitle = identity.companyIdentity
              ? identity.companyIdentity.name
              : identity.individualIdentity?.name || '';
            return (
              <CardIdentity
                key={president.id}
                subTitle={subTitle}
                title={i18nRoleValues(RepresentativeRole.PRESIDENT)}
                type={president.representativeIdentity.type}
              />
            );
          })}
          {otherRoles.map((otherRole) => {
            const identity = otherRole.representativeIdentity;
            const subTitle = identity.companyIdentity
              ? identity.companyIdentity.name
              : identity.individualIdentity?.name || '';

            // if role is CREATOR display in front "Collaborateur"
            const roleTitle =
              otherRole.role === RepresentativeRole.CREATOR
                ? i18nRoleValues(RepresentativeRole.VIEWER)
                : i18nRoleValues(otherRole.role);
            return (
              <CardIdentity
                key={identity.id}
                subTitle={subTitle}
                title={roleTitle}
                type={otherRole.representativeIdentity.type}
              />
            );
          })}
        </Stack>
      </CardBody>
      <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
        <Button variant="secondary" w="full" onClick={handleStepCancel}>
          {i18nGeneral('Back')}
        </Button>
        <Button data-cy="next" w="full" onClick={handleStepSubmit}>
          {i18nGeneral('Next')}
        </Button>
      </CardFooter>
    </Card>
  );
}

export type OnboardingExistingCompanySummaryProps = Props;
